import { CaretDownIcon, CaretUpIcon, CheckIcon } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import {
  PERMISSION_DISPLAY_NAMES,
  RESOURCE_PERMISSIONS,
  RoleFormData,
} from '@/types/sheets';

type PermissionChangeHandler = (arg: { [resource: string]: string[] }) => void;

interface RolePermissionsProps {
  control: Control<RoleFormData>;
  initialPermissions?: { [resource: string]: string[] };
}

const RolePermissions: React.FC<RolePermissionsProps> = ({
  control,
  initialPermissions = {},
}) => {
  const [expandedSections, setExpandedSections] = useState<{
    [resource: string]: boolean;
  }>(() => {
    // Initialize expanded sections based on initial permissions
    const initialExpanded: { [resource: string]: boolean } = {};
    Object.keys(RESOURCE_PERMISSIONS).forEach(resource => {
      initialExpanded[resource] = Boolean(
        initialPermissions[resource] && initialPermissions[resource].length > 0
      );
    });
    return initialExpanded;
  });

  const toggleSection = (resource: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [resource]: !prev[resource],
    }));
  };

  const isResourceSelected = (
    resource: string,
    permissions: { [resource: string]: string[] }
  ) => {
    return permissions[resource] && permissions[resource].length > 0;
  };

  const toggleResource = (
    resource: string,
    selectedPermissions: { [resource: string]: string[] },
    onChange: PermissionChangeHandler
  ) => {
    if (isResourceSelected(resource, selectedPermissions)) {
      const newPermissions = { ...selectedPermissions };
      delete newPermissions[resource];
      onChange(newPermissions);
    } else {
      onChange({
        ...selectedPermissions,
        [resource]: RESOURCE_PERMISSIONS[resource],
      });

      setExpandedSections(prev => ({
        ...prev,
        [resource]: true,
      }));
    }
  };

  const togglePermission = (
    resource: string,
    permission: string,
    selectedPermissions: { [resource: string]: string[] },
    onChange: PermissionChangeHandler
  ) => {
    const resourcePermissionsList = selectedPermissions[resource] || [];
    let newResourcePermissions;

    if (resourcePermissionsList.includes(permission)) {
      newResourcePermissions = resourcePermissionsList.filter(
        p => p !== permission
      );
    } else {
      newResourcePermissions = [...resourcePermissionsList, permission];
    }

    const newPermissions = {
      ...selectedPermissions,
      [resource]: newResourcePermissions,
    };

    if (newPermissions[resource].length === 0) {
      delete newPermissions[resource];
    }

    onChange(newPermissions);
  };

  const getColumnCount = (resource: string) => {
    return RESOURCE_PERMISSIONS[resource].length;
  };

  const getPermissionDisplayName = (permission: string) => {
    return (
      PERMISSION_DISPLAY_NAMES[permission] ||
      permission.charAt(0).toUpperCase() + permission.slice(1)
    );
  };

  const getResourceDisplayName = (resource: string) => {
    return resource.charAt(0).toUpperCase() + resource.slice(1);
  };

  return (
    <div className='mb-6'>
      <h3 className='mb-3 font-medium'>Assign Role Permissions</h3>

      <Controller
        name='permissions'
        control={control}
        render={({ field: { value, onChange } }) => (
          <div className='overflow-hidden border-b'>
            {Object.keys(RESOURCE_PERMISSIONS).map(resource => (
              <div key={resource} className='border-b last:border-b-0'>
                {/* Resource Header */}
                <div
                  className='flex items-center text-[16px] font-semibold justify-between py-3 px-4 cursor-pointer hover:bg-gray-5'
                  onClick={() => toggleSection(resource)}
                >
                  <div className='flex items-center'>
                    <div
                      className={`w-[18px] h-[18px] rounded-md mr-3 border flex items-center justify-center transition-colors
                        ${
                          isResourceSelected(resource, value)
                            ? 'bg-success-60 border-success-60'
                            : 'border-gray-40 hover:border-gray-50'
                        }`}
                      onClick={e => {
                        e.stopPropagation();
                        toggleResource(resource, value, onChange);
                      }}
                    >
                      {isResourceSelected(resource, value) && (
                        <CheckIcon size={16} className='text-white' />
                      )}
                    </div>
                    <span className='capitalize font-medium'>
                      {getResourceDisplayName(resource)}
                    </span>
                  </div>
                  {expandedSections[resource] ? (
                    <CaretUpIcon size={20} />
                  ) : (
                    <CaretDownIcon size={20} />
                  )}
                </div>

                {/* Resource Permissions */}
                {expandedSections[resource] && (
                  <div className='p-3'>
                    <div
                      className='grid bg-gray-5  text-[14px] font-medium gap-2 text-center border-b pb-2 mb-2'
                      style={{
                        gridTemplateColumns: `repeat(${getColumnCount(resource)}, 1fr)`,
                      }}
                    >
                      {RESOURCE_PERMISSIONS[resource].map(permission => (
                        <div key={permission} className='py-1'>
                          {getPermissionDisplayName(permission)}
                        </div>
                      ))}
                    </div>
                    <div
                      className='grid gap-2 rounded-b-md p-2'
                      style={{
                        gridTemplateColumns: `repeat(${getColumnCount(resource)}, 1fr)`,
                      }}
                    >
                      {RESOURCE_PERMISSIONS[resource].map(
                        (permission: string) => (
                          <div key={permission} className='flex justify-center'>
                            <div
                              className={`w-[18px] h-[18px] rounded-md border flex items-center justify-center cursor-pointer transition-colors
                                ${
                                  (value[resource] || []).includes(permission)
                                    ? 'bg-success-60 border-success-60'
                                    : 'border-gray-40 hover:border-gray-50'
                                }`}
                              onClick={() =>
                                togglePermission(
                                  resource,
                                  permission,
                                  value,
                                  onChange
                                )
                              }
                            >
                              {(value[resource] || []).includes(permission) && (
                                <CheckIcon size={16} className='text-white' />
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      />

      <Controller
        name='permissions'
        control={control}
        render={({ field: { value } }) => (
          <>
            {Object.keys(value).length === 0 && (
              <p className='text-destructive-50 text-sm mt-2'>
                Please select at least one resource permission
              </p>
            )}
          </>
        )}
      />
    </div>
  );
};

export default RolePermissions;
